/**
 * Side-effect wiring (doc 147 §9 Phase 1; §8.9.6 revised for theming).
 *
 * Connects the app store to the browser + Firebase: token injection,
 * appearance resolution (mode/density/graph-contrast), hash routing, the auth
 * lifecycle, the live manifest subscription, per-user state, and the ⌘K
 * keybinding. Kept out of `App.tsx` so the shell stays presentational and
 * testable. This module is Firebase-wiring — excluded from unit-test coverage
 * (exercised by the emulator), like `firebase/client.ts`.
 */
/* c8 ignore start */
import { appStore } from './state/store.js';
import { bindHashRouter } from './state/hashRouter.js';
import { applyAppearance, type DensityMode } from './theme/cssVariables.js';
import type { GraphContrast } from './theme/tokens.js';
import {
  resolveAppearance,
  loadCachedAppearanceSetting,
  saveCachedAppearanceSetting,
  type AppearanceSetting,
  type ModeSetting,
  type SystemPrefSource,
} from './state/appearance.js';
import { addRecent, togglePin } from './state/userState.js';
import { watchAuth, signInWithGoogle, signOutUser } from './firebase/auth.js';
import { legacyRedirect, navigate as navigateRoute, type Route } from './state/hashRouter.js';
import { pushTrail, type TrailEntry } from './state/workflowTrail.js';
import {
  watchManifestPointer,
  fetchManifest,
  watchUserState,
  saveUserState,
} from './firebase/firestore.js';
import { fetchLayout } from './graph/layoutClient.js';
import { callSetUserRole } from './firebase/callables.js';
import {
  watchBoards,
  watchSandboxes,
  watchUnderReviewSandboxesForAdmin,
  callSaveBoard,
  callDeleteBoard,
  callSaveSandbox,
  callDeleteSandbox,
  callSubmitSandboxForReview,
  callPublishSandbox,
  callSeedClickablePrototype,
  callDiffManifests,
  streamAskAtlas,
} from './firebase/atlasApi.js';
import { manifestSummary } from './state/selectors.js';
import type { AppActions } from './views/App.js';
import type { InternalUser } from './state/store.js';
import { recordViewToggle } from './state/viewToggleTelemetry.js';

/**
 * The trail entry a route is "about", or null when the route is not object-
 * scoped. Used by the route watcher to maintain the workflow trail.
 */
function trailEntryForRoute(route: Route): TrailEntry | null {
  if (route.kind === 'impact' || route.kind === 'entity') return { kind: 'entity', id: route.id };
  if (route.kind === 'workspace' && route.mode.kind === 'focused') {
    return { kind: 'entity', id: route.mode.entityId };
  }
  if (route.kind === 'testdata' && route.entityId) return { kind: 'entity', id: route.entityId };
  if (route.kind === 'surface') return { kind: 'surface', id: route.id };
  if (route.kind === 'cloud-function') return { kind: 'cloud-function', id: route.id };
  if (route.kind === 'event-type') return { kind: 'event-type', id: route.id };
  if (route.kind === 'firestore-path') return { kind: 'firestore-path', id: route.entityId };
  if (route.kind === 'feature') return { kind: 'feature', id: route.id };
  if (route.kind === 'flag-catalog' && route.flagId) return { kind: 'flag', id: route.flagId };
  if (route.kind === 'hub' && route.id) return { kind: 'hub', id: route.id };
  if (route.kind === 'statemachine' && route.machineId) {
    return { kind: 'statemachine', id: route.machineId };
  }
  if ('focus' in route && typeof route.focus === 'string') return { kind: 'entity', id: route.focus };
  if (route.kind === 'sandbox' && route.seedEntity) return { kind: 'entity', id: route.seedEntity };
  return null;
}

let manifestUnsub: (() => void) | null = null;
let userStateUnsub: (() => void) | null = null;
let boardsUnsub: (() => void) | null = null;
let sandboxesUnsub: (() => void) | null = null;
let adminReviewQueueUnsub: (() => void) | null = null;

/* ─── Appearance plumbing ─────────────────────────────────────────────── */

const mql =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

const sysSource: SystemPrefSource | null = mql ? { prefersDark: () => mql.matches } : null;

const storage =
  typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;

/** Pull the live setting out of the store as an AppearanceSetting. */
function currentSetting(): AppearanceSetting {
  const s = appStore.getSnapshot();
  return { mode: s.mode, density: s.density, graphContrast: s.graphContrast };
}

/** Resolve current setting against the system pref and apply it everywhere. */
function reapplyAppearance(): void {
  applyAppearance(resolveAppearance(currentSetting(), sysSource));
}

/** Update the store + localStorage + apply; optionally persist to Firestore. */
function updateAppearance(patch: Partial<AppearanceSetting>): void {
  const next = { ...currentSetting(), ...patch };
  appStore.setState({
    mode: next.mode,
    density: next.density,
    graphContrast: next.graphContrast,
  });
  if (storage) saveCachedAppearanceSetting(storage, next);
  applyAppearance(resolveAppearance(next, sysSource));
  const uid = appStore.getSnapshot().user?.uid;
  if (uid) void saveUserState(uid, patch);
}

function startManifestWatch(): void {
  manifestUnsub?.();
  appStore.setState({ manifestStatus: 'loading' });
  manifestUnsub = watchManifestPointer(
    (pointer) => {
      if (!pointer) {
        appStore.setState({
          manifestStatus: 'error',
          manifestError: 'No manifest has been published yet.',
        });
        return;
      }
      fetchManifest(pointer)
        .then((manifest) => {
          appStore.setState({ manifest, manifestStatus: 'ready', manifestError: null });
        })
        .catch((err: unknown) => {
          appStore.setState({
            manifestStatus: 'error',
            manifestError: err instanceof Error ? err.message : 'Failed to load the manifest.',
          });
        });
      // Pre-computed constellation layout (PERF-03) — best-effort; a missing
      // or failed layout just means the graph uses the deterministic circular
      // fallback, so a rejection here is swallowed (never left unhandled).
      if (pointer.layoutUrl) {
        fetchLayout(pointer.layoutUrl)
          .then((layout) => appStore.setState({ layout }))
          .catch((err: unknown) => {
            console.error('[atlas] layout fetch failed; using circular fallback:', err);
          });
      }
    },
    (err) => {
      appStore.setState({ manifestStatus: 'error', manifestError: err.message });
    },
  );
}

function startUserStateWatch(uid: string): void {
  userStateUnsub?.();
  userStateUnsub = watchUserState(uid, (state) => {
    // Firestore wins over the localStorage cache for an authenticated user.
    updateAppearance({
      mode: state.mode,
      density: state.density,
      graphContrast: state.graphContrast,
    });
    appStore.setState({
      recents: state.recents,
      pinned: state.pinned,
      view: state.view,
    });
  });
}

function startBoardsWatch(uid: string): void {
  boardsUnsub?.();
  boardsUnsub = watchBoards(uid, (boards) => appStore.setState({ boards }));
}

function startSandboxesWatch(uid: string): void {
  sandboxesUnsub?.();
  sandboxesUnsub = watchSandboxes(uid, (sandboxes) => appStore.setState({ sandboxes }));
}

function startAdminReviewQueueWatch(): void {
  adminReviewQueueUnsub?.();
  adminReviewQueueUnsub = watchUnderReviewSandboxesForAdmin((adminReviewQueue) =>
    appStore.setState({ adminReviewQueue }),
  );
}

/** Wire the store to the browser + Firebase. Idempotent enough for one call. */
export function bootstrapAtlas(): void {
  // 1. Hydrate appearance from the localStorage cache (the FOUC script in
  //    index.html already painted matching attrs; this writes the CSS vars).
  const cached = storage ? loadCachedAppearanceSetting(storage) : { mode: 'auto', density: 'compact', graphContrast: 'standard' } as AppearanceSetting;
  appStore.setState({
    mode: cached.mode,
    density: cached.density,
    graphContrast: cached.graphContrast,
    systemPrefersDark: mql ? mql.matches : true,
  });
  applyAppearance(resolveAppearance(cached, sysSource));

  // 2. Live system-pref tracking — only re-applies when mode === 'auto'.
  if (mql && typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', (e: MediaQueryListEvent) => {
      appStore.setState({ systemPrefersDark: e.matches });
      if (appStore.getSnapshot().mode === 'auto') reapplyAppearance();
    });
  }

  bindHashRouter((route) => {
    // Legacy routes (e.g. the old `#constellation` shortcut) one-way
    // redirect to their canonical equivalent. The redirect triggers a
    // fresh hashchange. (Old `#workspace/X/sheet` links are redirected
    // even earlier, inside parseHash itself — see hashRouter.ts.)
    const redirect = legacyRedirect(route);
    if (redirect) {
      navigateRoute(redirect);
      return;
    }
    // Maintain the workflow trail: every time a new focused object becomes
    // the route's target, push it. Dedupe + cap handled by `pushTrail`.
    const entry = trailEntryForRoute(route);
    const prev = appStore.getSnapshot().entityTrail;
    const entityTrail = entry ? pushTrail(prev, entry) : prev;
    appStore.setState({ route, entityTrail });
  });

  watchAuth(
    (user: InternalUser | null) => {
      appStore.setState({ user, authStatus: 'ready' });
      if (user) {
        appStore.setState({ sessionExpired: false });
        startManifestWatch();
        startUserStateWatch(user.uid);
        startBoardsWatch(user.uid);
        startSandboxesWatch(user.uid);
        if (user.role === 'admin') {
          startAdminReviewQueueWatch();
        } else {
          adminReviewQueueUnsub?.();
          adminReviewQueueUnsub = null;
          appStore.setState({ adminReviewQueue: [] });
        }
      } else {
        manifestUnsub?.();
        userStateUnsub?.();
        boardsUnsub?.();
        sandboxesUnsub?.();
        adminReviewQueueUnsub?.();
        appStore.setState({ boards: [], sandboxes: [], adminReviewQueue: [] });
      }
    },
    () => appStore.setState({ sessionExpired: true }),
  );

  // ⌘K / ? / Esc / arrows / etc. are now dispatched by App.tsx via the
  // declarative keyboardMap — no global listener here. See state/keyboardMap.ts.
}

/** Firebase-backed action bag passed to <App />. */
export const appActions: AppActions = {
  signIn: () => signInWithGoogle(),
  signOut: () => {
    void signOutUser();
  },
  setUserRole: async (uid, role) => {
    await callSetUserRole({ uid, role });
  },
  setDensity: (mode: DensityMode) => updateAppearance({ density: mode }),
  setMode: (mode: ModeSetting) => updateAppearance({ mode }),
  setGraphContrast: (level: GraphContrast) => updateAppearance({ graphContrast: level }),
  togglePin: (entityId: string) => {
    const state = appStore.getSnapshot();
    const pinned = togglePin(state.pinned, entityId);
    const recents = addRecent(state.recents, entityId);
    appStore.setState({ pinned, recents });
    if (state.user) void saveUserState(state.user.uid, { pinned, recents });
  },
  openSearch: () => appStore.setState({ searchOpen: true }),
  closeSearch: () => appStore.setState({ searchOpen: false }),
  setActiveEntityId: (id: string | null) => appStore.setState({ activeEntityId: id }),
  setActiveDomainId: (id: string | null) => appStore.setState({ activeDomainId: id }),
  setActiveLayerFilter: (filter: string | null) => appStore.setState({ activeLayerFilter: filter }),
  askAtlas: (question: string) => {
    const manifest = appStore.getSnapshot().manifest;
    const summary = manifest
      ? {
          entityCount: manifestSummary(manifest).entityCount,
          domainCount: manifestSummary(manifest).domainCount,
          domainNames: manifest.domains.map((d) => d.name),
        }
      : { entityCount: 0, domainCount: 0, domainNames: [] };
    const knownEntityIds = manifest ? manifest.entities.map((e) => e.id) : [];
    return streamAskAtlas(question, summary, knownEntityIds);
  },
  runDiff: (fromSha: string, toSha: string) => callDiffManifests(fromSha, toSha),
  saveBoard: (name: string, entityIds: string[]) => callSaveBoard(name, entityIds),
  deleteBoard: (boardId: string) => callDeleteBoard(boardId),
  saveSandbox: (sandboxId: string, name: string, ops: unknown[]) =>
    callSaveSandbox(sandboxId, name, ops),
  deleteSandbox: (sandboxId: string) => callDeleteSandbox(sandboxId),
  submitSandboxForReview: (sandboxId: string) => callSubmitSandboxForReview(sandboxId),
  publishSandbox: (sandboxId: string, ownerUid?: string) => callPublishSandbox(sandboxId, ownerUid),
  generateClickablePrototype: (overlayManifest) => callSeedClickablePrototype(overlayManifest),
  toggleView: () => {
    const snap = appStore.getSnapshot();
    const next = snap.view === '3d' ? '2d' : '3d';
    const now = Date.now();
    // Telemetry — record how long the user spent in the previous view.
    // Cheap, localStorage-only signal for "do users actually open 3D?"
    recordViewToggle({
      from: snap.view,
      to: next,
      durationInPrevMs: now - lastToggleAt,
      at: now,
    });
    lastToggleAt = now;
    appStore.setState({ view: next });
    if (snap.user?.uid) void saveUserState(snap.user.uid, { view: next });
    // If the toggle fires while the user is somewhere else (e.g. ImpactView),
    // surface the change by routing them to the workspace — the toggle only
    // affects that screen, and otherwise the keypress would feel like a no-op.
    if (snap.route.kind !== 'workspace') {
      navigateRoute({ kind: 'workspace', mode: { kind: 'idle' } });
    }
  },
};

/** Module-level wall clock — used to time how long the user spent in the
 *  previous view before each toggle. Initialised at first import. */
let lastToggleAt = Date.now();
/* c8 ignore stop */
